import { useEffect } from "react";
import { createPortal } from "react-dom";

/**
 * Shared modal shell:
 *  - Rendered via React portal on document.body so it's never trapped inside a
 *    parent with `transform`/`filter` (which would break `position: fixed` and
 *    make the modal appear at the top of the document instead of the viewport).
 *  - Fully solid backdrop (black by default, white in light theme via bg-background).
 *  - Vertically centered in the viewport with a scroll fallback for tall content.
 *  - Locks body scroll while open so the page underneath doesn't jump.
 *  - Closes on ESC and on backdrop click.
 */
export default function ModalShell({
  open,
  onClose,
  children,
  maxWidth = "max-w-xl",
  testId,
}) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose && onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const content = (
    <>
      {/* Fully solid backdrop — fixed to viewport so it always covers everything even when the modal content scrolls. */}
      <div
        className="fixed inset-0 z-[99] bg-background"
        onClick={onClose}
        data-testid={testId ? `${testId}-backdrop` : undefined}
      />
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6 overflow-y-auto pointer-events-none"
        data-testid={testId}
      >
        <div
          className={`relative w-full ${maxWidth} bg-surface border border-border rounded-sm p-8 shadow-2xl my-auto pointer-events-auto`}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      </div>
    </>
  );

  return createPortal(content, document.body);
}
